import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { type OutbreakCase, type CaseStatus, type VariantKey } from "@/data/cases";

const CACHE_KEY = "hvi:cases:v4";

function mapRow(r: any): OutbreakCase | null {
  if (r.lat == null || r.lng == null) return null;
  return {
    id: Number(r.external_id ?? 0) || Math.floor(Math.random() * 1e9),
    country: r.country,
    city: r.city ?? undefined,
    lat: Number(r.lat),
    lng: Number(r.lng),
    status: (r.status ?? "monitoring") as CaseStatus,
    nationality: r.nationality ?? undefined,
    onsetDate: r.onset_date ?? undefined,
    // Used only for UI ordering / timeline filtering when onset_date is missing —
    // never for epi charts (would cause a fake spike on the report date).
    effectiveDate: r.onset_date ?? r.reported_date ?? r.created_at ?? undefined,
    deaths: Number(r.deaths ?? 0),
    notes: r.notes ?? r.summary ?? "",
    source: r.source ?? undefined,
    onboard: !!r.onboard,
    variant: (r.variant ?? "unknown") as VariantKey,
    _dbId: r.id,
  } as OutbreakCase & { _dbId?: string };
}

function loadCache(): OutbreakCase[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function useCases() {
  // Start empty on SSR; on client, hydrate from localStorage so a refresh
  // shows the previous live snapshot instantly (no seed-data flash).
  const [cases, setCases] = useState<OutbreakCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cached = loadCache();
    if (cached.length > 0) setCases(cached);

    let cancelled = false;
    let isDbBacked = cached.length > 0;

    const fetchAll = async () => {
      const { data, error } = await supabase
        .from("cases")
        .select("*")
        .order("onset_date", { ascending: true });
      if (cancelled) return;
      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }
      if (data && data.length > 0) {
        const mapped = data.map(mapRow).filter(Boolean) as OutbreakCase[];
        isDbBacked = true;
        setCases(mapped);
        try {
          window.localStorage.setItem(CACHE_KEY, JSON.stringify(mapped));
        } catch {
          /* quota or unavailable */
        }
      }
      setLoading(false);
    };

    fetchAll();

    // Debounce realtime bursts so the UI doesn't flash partial counts while
    // multiple rows are inserted/updated in quick succession.
    let refetchTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefetch = () => {
      if (refetchTimer) clearTimeout(refetchTimer);
      refetchTimer = setTimeout(() => {
        refetchTimer = null;
        fetchAll();
      }, 300);
    };

    const channel = supabase
      .channel(`cases-changes-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cases" },
        (payload) => {
          // If we're still on SEED_CASES, don't mutate piecemeal — wait for a
          // full refetch so the user never sees a partial DB-only snapshot.
          if (!isDbBacked) {
            scheduleRefetch();
            return;
          }
          setCases((prev) => {
            if (payload.eventType === "DELETE") {
              const oldId = (payload.old as any)?.id;
              return prev.filter((c: any) => c._dbId !== oldId);
            }
            const mapped = mapRow(payload.new as any);
            if (!mapped) return prev;
            const idx = prev.findIndex((c: any) => c._dbId === (mapped as any)._dbId);
            if (idx === -1) return [...prev, mapped];
            const next = prev.slice();
            next[idx] = mapped;
            return next;
          });
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);

  return { cases, loading, error };
}
